import z from 'zod'
import { WORKING_RIGHT_TYPES } from '../pages/Dashboard/RegistrationModals/components/helper'
import {
    AccreditationDocuments,
    LocalTeacherEmploymentSetting,
    TeacherFields,
} from '../redux/types'
import { ChipLabelMap } from './types-school'
import { ausStates } from './accreditation-api'
import { fieldStates } from '../schemas/teacherReg'
import { dbBoolean, dbBooleanMap, dbString } from '../schemas/helper'

//#region Teachers node information
export enum TeacherAccountState {
    INCOMPLETE = 'incomplete',
    SUBMITTED = 'submitted',
    APPROVED = 'approved',
    REJECTED = 'rejected',
    ONHOLD = 'onHold',
    DELETED = 'deleted',
    offboarded = 'offboarded',
}

export const teacherAccountState = z
    .enum([
        'incomplete',
        'submitted',
        'approved',
        'rejected',
        'onHold',
        'deleted',
        'offboarded',
    ] as const)
    .default('incomplete')
export type TeacherAccountState_v2 = z.infer<typeof teacherAccountState>

export enum FieldState {
    APPROVED = 'approved',
    SUBMITTED = 'submitted',
    REJECTED = 'rejected',
    EXPIRED = 'expired',
    INCOMPLETE = 'incomplete',
}

export const setLocationSchema = z.object({
    country: z.string(),
    homeState: z.string(),
    lat: z.number().default(0),
    long: z.number().default(0),
})
export type SetLocation = z.infer<typeof setLocationSchema>

export const educatorLevels = z.enum([
    'certIII',
    'earlyChildhoodTrained',
    'diploma',
] as const)
export type EducatorEmploymentLevel = z.infer<typeof educatorLevels>

export const educatorLevelLabels: Record<EducatorEmploymentLevel, string> = {
    certIII: 'Cert III',
    earlyChildhoodTrained: 'ECT',
    diploma: 'Diploma',
}

export const employmentSettingTypes = z.enum([
    'school',
    'earlyChildcare',
    'supportStaff',
] as const)
export const employmentSettingStates = teacherAccountState
export const employmentSettingSchema = z
    .object({
        locationState: ausStates.default('VIC'),
        state: employmentSettingStates,
    })
    .and(
        z.discriminatedUnion('type', [
            z.object({
                type: employmentSettingTypes.extract(['school']),
            }),
            z.object({
                type: employmentSettingTypes.extract(['supportStaff']),
                hasQualification: z.boolean().default(false),
            }),
            z.object({
                type: employmentSettingTypes.extract(['earlyChildcare']),
                level: educatorLevels.optional(),
            }),
        ])
    )
export const employmentSettingsSchema = z.record(
    z.string(),
    employmentSettingSchema
)
export type EmploymentSetting = z.infer<typeof employmentSettingSchema>
export type EmploymentSettings = z.infer<typeof employmentSettingsSchema>

//#endregion

//#region Availability
export enum TeacherAvailabilityStateForDate {
    unavailable = 'unavailable',
    available = 'available',
    booked = 'booked',
}

export type TeacherAvailability = {
    [date: string]: string
}
//#endregion

//#region Bookings
export enum TeacherPlacementState {
    confirmed = 'confirmed',
    enRoute = 'enRoute',
    nearby = 'nearby',
    inProgress = 'inProgress',
    earlyCheckout = 'earlyCheckout',
    completed = 'completed',
    cancelled = 'cancelled',
}

export enum ConfirmedPlacementState {
    confirmed = 'confirmed',
    cancelledBySchool = 'cancelledBySchool',
    cancelledByTeacher = 'cancelledByTeacher',
}

export type TeacherPlacement = {
    date?: string
    notified?: number
    schoolUid?: string
}

export type TeacherPlacementKeys = {
    [year: string]: {
        [date: string]: {
            [placementKey: string]: TeacherPlacement
        }
    }
}
//#endregion Bookings

export enum NSWAccreditationLevel {
    BAND1 = 'band1',
    BAND2 = 'band2',
    BAND3 = 'band3',
    UNSURE = `I'm Unsure`,
}

export type ECCAccreditationLevel =
    | 'certIII'
    | 'earlyChildhoodTrained'
    | 'diploma'

export const ECCQualificationLevels = [
    'certThree',
    'dimploma',
    'other',
    'noFormalTraining',
]

export const ECCQualToTitle = (qual?: string | null) => {
    switch (qual) {
        case 'certThree':
            return 'Certificate III'
        case 'dimploma':
            return 'Diploma'
        case 'earlyChildhoodTrained':
            return 'Early Childhood Trained'
        case 'other':
            return 'Other'
        case 'noFormalTraining':
            return 'No Formal Training'
        default:
            return '-'
    }
}

export const ESQualifications = [
    'certThree',
    'certFour',
    'bachelorsDegree',
    'mastersDegree',
    'other',
] as const
export const ESQualificationOptions = [
    { label: 'Certificate III', value: 'certThree' },
    { label: 'Certificate IV', value: 'certFour' },
    { label: `Bachelors Degree`, value: 'bachelorsDegree' },
    { label: 'Masters Degree', value: 'mastersDegree' },
    { label: 'Other', value: 'other' },
]

export enum TeacherRequestTypes {
    NAME = 'actualName',
    ACC_DOC_UPDATE = 'accreditationDocument',
    NEW_ACCREDITATION = 'newAccreditation',
    HOME_ADDRESS = 'homeAddress',
    WORKING_RIGHTS = 'workingRights',
    PROFILE_PICTURE = 'profilePicture',
}

export interface TeacherRequest {
    actionedTimeStamp?: number
    state: string
    timeStamp: number
    teacherName: string
    teacherUid: string
    type: TeacherRequestTypes
}

export enum NameChangeReason {
    LASTNAME = 'I changed my last name',
    FIRSTNAME = 'I changed my first name',
    BOTHNAMES = 'I changed both my names',
    INCORRECT = 'My name is incorrect',
}
export interface TeacherNameChangeRequest extends TeacherRequest {
    reason?: NameChangeReason
    firstName: string
    lastName: string
    firstNameActual: string
    url: string
}

export interface TeacherPictureChangeRequest extends TeacherRequest {
    url: string | null
}
export interface TeacherAccreditationChangeRequest extends TeacherRequest {
    documentUid?: string
    url?: string | null
}
export interface TeacherWorkingRightsChangeRequest extends TeacherRequest {
    workingRightsType: WORKING_RIGHT_TYPES
    url?: string
}
export interface TeacherLocationChangeRequest extends TeacherRequest {
    location: TeacherFields['homeAddress']
}

export const teacherTypes = z
    .enum(['Primary', 'Secondary', 'Primary & Secondary'] as const)
    .default('Primary & Secondary')

export type TeacherType = z.infer<typeof teacherTypes>

export const teacherTypeData = z
    .object({
        value: teacherTypes,
    })
    .transform(
        (v) =>
            v ?? {
                value: 'Primary & Secondary',
            }
    )

export type TeacherPublicInfo = Pick<
    TeacherFields,
    | 'accountState'
    | 'completionRate'
    | 'profilePicture'
    | 'firstName'
    | 'firstNameActual'
    | 'lastName'
    | 'type'
    | 'contact'
    | 'firstEmergencyContact'
    | 'secondEmergencyContact'
    | 'employmentSetting'
    | 'completedSchools'
    | 'favouriteSchools'
    | 'setLocation'
    | 'profile'
    | 'supportStaffProfile'
    | 'educatorProfile'
    | 'languages'
> & {
    accreditation?: AccreditationDocuments
    setting: LocalTeacherEmploymentSetting
}

export const workingHistoryEmploymentTypeOptions = z.enum(
    [
        'Full-time',
        'Part-time',
        'Casual',
        'Contract',
        'Placement',
        'Volunteer',
    ] as const,
    'Please select one of the options'
)
export const teacherWorkingHistoryEntrySchema = z.object({
    role: z.string('Incomplete*'),
    employmentType: workingHistoryEmploymentTypeOptions,
    employer: z.string('Incomplete*').min(1, 'Incomplete*'),

    employerDetails: z
        .object({
            coordinates: z
                .object({
                    lat: z.number(),
                    lng: z.number(),
                })
                .nullable()
                .default(null),
            country: z.string().nullable().default(null),
            locationState: z.string().nullable().default(null),
            address: z.string().nullable().default(null),
            website: z.url().nullable().default(null),
        })
        .nullable()
        .default(null),

    startDate: z.string().describe('Month and year started with employer'),
    endDate: z
        .string()
        .nullable()
        .default(null)
        .describe(
            'Month and year ended with employer. If null, currently in role'
        ),
    supervisorName: z.string('Incomplete*').min(1, 'Incomplete*'),
    supervisorRole: z.string('Incomplete*').min(1, 'Incomplete*'),
    supervisorContact: z.string('Incomplete*').min(1, 'Incomplete*'),
    supervisorEmail: z.string('Incomplete*').nullable().default(null),
    notes: z.string().nullable().default(null),
})
export const teacherWorkingHistoryEntriesSchema = z.record(
    z.string(),
    teacherWorkingHistoryEntrySchema
)
export type TeacherWorkingHistoryEntry = z.infer<
    typeof teacherWorkingHistoryEntrySchema
>
export type TeacherWorkingHistoryEntries = z.infer<
    typeof teacherWorkingHistoryEntriesSchema
>

export const teacherWorkingHistory = z
    .object({
        state: fieldStates,
        entryCount: z.number().optional(),
        entries: teacherWorkingHistoryEntriesSchema.optional(),
    })
    .optional()
    .transform((v) => ({
        state: v?.state || 'incomplete',
        entries: v?.entries,
        entryCount: Object.keys(v?.entries || {}).length,
    }))
// .transform((v) =>
//     v
//         ? {
//               state: new Set([
//                   'incomplete',
//                   'submitted',
//                   null,
//                   undefined,
//               ]).has(v.state)
//                   ? !!v.entries
//                       ? 'submitted'
//                       : 'incomplete'
//                   : v.state,
//               entries: v.entries || {},
//               //   ...v,
//           }
//         : {
//               state: 'incomplete' as FieldState_v2,
//               entries: {},
//           }
// )
export type TeacherWorkingHistory = z.infer<typeof teacherWorkingHistory>

// #region Teacher Profile

export const TEACHING_EXPERIENCE_OPTIONS: ChipLabelMap = {
    compositeMultiAgeClasses: 'Composite/multi-age classes',
    specialistSchoolSettings: 'Specialist school settings',
    fivePlusYearsClassroomExperience: '5+ years classroom experience',
} as const
export const BEHAVIOUR_CLASSROOM_MANAGEMENT_OPTIONS: ChipLabelMap = {
    diverseClassroomBehaviours: 'Diverse classroom behaviours',
    highBehaviourSupportSettings: 'High behavioural support settings',
    restorativePractices: 'Restorative practices',
    traumaInformedPractice: 'Trauma-informed practice',
    positiveBehaviourFrameworks: 'Positive behaviour frameworks (e.g. SWPBS)',
    culturallySocioEconomicallyDiverseCommunities:
        'Culturally and socio-economically diverse communities',
} as const

export const CRT_LEARNING_INCLUSION_OPTIONS: ChipLabelMap = {
    autismSupport: 'Autism support',
    adhdSupport: 'ADHD support',
    ealdLearnerSupport: 'EAL/D learner support',
    iepsIlps: 'IEPs / ILPs',
    learningDisabilitiesSupport: 'Learning disabilities support',
    oneOnOneStudentSupport: 'One-on-one student support',
    smallGroupInterventionSupport: 'Small group intervention support',
    workingWithLearningSupportStaff: 'Working with learning support staff',
} as const

const qualifyingAreaSchema = z.record(
    z.string().describe('Qualifying subject title'),
    z.object({
        label: z.string(),
        minYear: z.number(),
        maxYear: z.number(),
    })
)
export type QualifyingArea = z.infer<typeof qualifyingAreaSchema>

const languageProficiency = z.enum(['conversational', 'fluent'] as const)
export type LanguageProficiency = z.infer<typeof languageProficiency>
export const languagesSchema = z.record(
    z.string(),
    z.object({
        language: z.string(),
        proficiency: languageProficiency,
    })
)
export type Languages = z.infer<typeof languagesSchema>

const preferredYearsSchema = z.object({
    min: z.number(),
    max: z.number(),
})

export const teacherProfileSchema = z.object({
    about: dbString,
    years: preferredYearsSchema.nullable().default(null),
    favouriteSubjects: dbBooleanMap,
    supportsAdditionalNeeds: dbBoolean,

    teachingExperience: dbBooleanMap,
    behaviourClassroomManagementExperience: dbBooleanMap,
    learningInclusionExperience: dbBooleanMap,
    qualifiedTeachingAreas: qualifyingAreaSchema.nullable().default(null),
})

export type TeacherProfile = z.infer<typeof teacherProfileSchema>

// #endregion

//#region Support Staff Profile

export const supportStaffProfileSchema = z.object({
    about: dbString,
    years: preferredYearsSchema
        .nullable()
        .default(null)
        .describe('Preferred supporting levels'),

    behaviourManagementExperience: dbBooleanMap,
    learningInclusionExperience: dbBooleanMap,
})
export type SupportStaffProfile = z.infer<typeof supportStaffProfileSchema>

export type Language = {
    language: string
    proficiency: LanguageProficiency
}

export const ES_BEHAVIOUR_CLASSROOM_OPTIONS: ChipLabelMap = {
    behaviourSupportPlans: 'Supporting students with behaviour support plans',
    highBehaviourSupportSettings:
        'Experience in high behavioural support settings',
    deEscalationStrategies: 'De-escalation strategy experience',
    emotionalRegulation: 'Supporting emotional regulation',
    restorativePractices: 'Restorative practices approach',
    positiveBehaviourFrameworks: 'Positive behaviour frameworks (e.g. SWPBS)',
} as const
export const ES_LEARNING_INCLUSION_OPTIONS: ChipLabelMap = {
    learningDisabilities: 'Supporting students with learning disabilities',
    autismSpectrum: 'Supporting students on the autism spectrum',
    adhd: 'Supporting students with ADHD',
    ealdLearners: 'Supporting EAL/D learners',
    iepIlpImplementation: 'Implementing IEPs / ILPs',
    learningSupportTeams: 'Working alongside learning support teams',
    oneOnOneSupport: 'One-on-one student support',
    smallGroupIntervention: 'Small group intervention support',
} as const
//#endregion

//#region Early Childcare Educator Profile

export const educatorProfileSchema = z.object({
    about: dbString,
    approachToLearning: dbBooleanMap,
    personalInterests: dbBooleanMap,
})
export type EducatorProfile = z.infer<typeof educatorProfileSchema>

//#endregion

//#region Derived User Profile TYpes

export type TeacherProfileObject = Pick<TeacherFields, 'profile' | 'type' | 'languages'>
export type SupportStaffProfileObject = Pick<
    TeacherFields,
    'supportStaffProfile' | 'type' | 'languages'
>

export type EducatorProfileObject = Pick<
    TeacherFields,
    'educatorProfile' | 'languages'
>

//#endregion
